import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Activity, Users, Settings, LogOut,
  Info, Mail, Home, Menu, Camera, ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/* ─── Avatar persistence ─────────────────────────────────────────── */
const AVATAR_KEY = 'user_avatar';
const getStoredAvatar = () => { try { return localStorage.getItem(AVATAR_KEY) || null; } catch { return null; } };
const saveAvatar = (dataUrl) => {
  try {
    localStorage.setItem(AVATAR_KEY, dataUrl);
    window.dispatchEvent(new CustomEvent('avatarUpdated', { detail: dataUrl }));
  } catch { }
};

/* ─── Nav config ─────────────────────────────────────────────────── */
const navItems = [
  { label: 'Dashboard',    icon: Home,     path: '/dashboard' },
  { label: 'New Analysis', icon: Activity, path: '/new-analysis' },
  { label: 'Patient',      icon: Users,    path: '/patient' },
];
const utilItems = [
  { label: 'Account Settings', icon: Settings, path: '/account' },
  { label: 'About',            icon: Info,     path: '/about' },
  { label: 'Contact',          icon: Mail,     path: '/contact' },
];

/* ─── Global styles (light theme) ───────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --sidebar-w:     260px;
    --header-h:      54px;
    --bottom-nav-h:  60px;

    --safe-top:    env(safe-area-inset-top, 0px);
    --safe-bottom: env(safe-area-inset-bottom, 0px);

    /* Light palette */
    --bg-base:       #f5f7fa;
    --bg-surface:    #ffffff;
    --bg-elevated:   #f0f3f8;
    --bg-hover:      #e8edf5;
    --bg-active:     #dde5f2;

    --blue:          #2563eb;
    --blue-light:    rgba(37,99,235,0.1);
    --blue-border:   rgba(37,99,235,0.25);
    --blue-dark:     #1d4ed8;
    --cyan:          #0891b2;

    --border:        rgba(0,0,0,0.08);
    --border-md:     rgba(0,0,0,0.14);

    --text-hi:       #0f172a;
    --text-mid:      #475569;
    --text-lo:       #94a3b8;

    --shadow-sm:     0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05);
    --shadow-md:     0 4px 16px rgba(0,0,0,0.10);
    --shadow-sidebar: 4px 0 24px rgba(0,0,0,0.12);

    --radius:        10px;
    --radius-sm:     7px;
    --radius-xs:     5px;
    --ease:          cubic-bezier(0.4,0,0.2,1);
    --t:             0.17s;
  }

  html, body {
    height: 100%;
    background: var(--bg-base);
    color: var(--text-hi);
    font-family: 'DM Sans', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: transparent;
    overflow: hidden;
  }

  button { font-family: inherit; border: none; cursor: pointer; background: none; }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: var(--border-md); border-radius: 4px; }

  /* ── Shell ──────────────────────────────────────── */
  .cx-shell {
    position: fixed;
    top: var(--safe-top);
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    overflow: hidden;
    background: var(--bg-base);
  }

  /* ── Backdrop ───────────────────────────────────── */
  .cx-backdrop {
    position: fixed; inset: 0; z-index: 199;
    background: rgba(15,23,42,0.35);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.24s var(--ease);
  }
  .cx-backdrop[data-open="true"] {
    opacity: 1;
    pointer-events: auto;
  }

  /* ── Sidebar ────────────────────────────────────── */
  /* FIX 3: top: 0 so sidebar covers full screen including status bar area */
  .cx-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: var(--sidebar-w);
    background: var(--bg-surface);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    z-index: 200;
    transform: translateX(-100%);
    transition: transform 0.27s var(--ease), box-shadow 0.27s var(--ease);
    will-change: transform;
  }
  .cx-sidebar[data-open="true"] {
    transform: translateX(0);
    box-shadow: var(--shadow-sidebar);
  }

  .cx-sidebar::after {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--blue) 0%, var(--cyan) 100%);
  }

  /* ── Sidebar header ─────────────────────────────── */
  /* FIX 3: padding-top accounts for iOS status bar so logo isn't under it */
  .cx-sb-top {
    padding: 20px 16px 14px;
    padding-top: calc(20px + env(safe-area-inset-top));
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 10px;
    flex-shrink: 0;
  }
  .cx-logo { height: 26px; width: auto; }

  /* ── Nav ────────────────────────────────────────── */
  .cx-nav {
    flex: 1; overflow-y: auto;
    padding: 14px 10px;
  }
  .cx-nav-group + .cx-nav-group { margin-top: 20px; }

  .cx-section-lbl {
    display: block;
    font-size: 0.62rem; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--text-lo);
    padding: 0 8px; margin-bottom: 4px;
  }

  .cx-nav-btn {
    width: 100%;
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px; min-height: 44px;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-mid);
    font-size: 0.875rem; font-weight: 400;
    text-align: left;
    transition: all var(--t) var(--ease);
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  .cx-nav-btn:hover, .cx-nav-btn:focus-visible {
    background: var(--bg-hover);
    color: var(--text-hi);
    border-color: var(--border);
    outline: none;
  }
  .cx-nav-btn.active {
    background: var(--blue-light);
    color: var(--blue-dark);
    border-color: var(--blue-border);
    font-weight: 500;
  }
  .cx-nav-btn.active svg:first-child { color: var(--blue); }

  .cx-chevron {
    margin-left: auto; color: var(--blue);
    opacity: 0; transform: translateX(-3px);
    transition: all var(--t) var(--ease);
    flex-shrink: 0;
  }
  .cx-nav-btn.active .cx-chevron,
  .cx-nav-btn:hover .cx-chevron { opacity: 1; transform: translateX(0); }

  .cx-divider { height: 1px; background: var(--border); margin: 12px 6px; }

  /* ── Sidebar footer ─────────────────────────────── */
  .cx-sb-footer {
    border-top: 1px solid var(--border);
    padding: 10px;
    /* FIX 3: push footer above home indicator */
    padding-bottom: calc(10px + env(safe-area-inset-bottom));
    flex-shrink: 0;
  }

  .cx-user-card {
    display: flex; align-items: center; gap: 10px;
    padding: 10px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    margin-bottom: 8px;
    box-shadow: var(--shadow-sm);
  }

  .cx-avatar-wrap {
    position: relative; flex-shrink: 0;
    width: 36px; height: 36px;
    cursor: pointer; touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  .cx-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.75rem; font-weight: 600; color: #fff;
    overflow: hidden;
    background:
      linear-gradient(var(--bg-elevated), var(--bg-elevated)) padding-box,
      linear-gradient(135deg, var(--blue), var(--cyan)) border-box;
    border: 2px solid transparent;
    transition: border-color var(--t) var(--ease);
  }
  .cx-avatar img { width: 100%; height: 100%; object-fit: cover; }
  .cx-avatar-wrap:hover .cx-avatar,
  .cx-avatar-wrap:focus-visible .cx-avatar { border-color: var(--blue); }
  .cx-avatar-overlay {
    position: absolute; inset: 0; border-radius: 50%;
    background: rgba(15,23,42,0.55);
    display: flex; align-items: center; justify-content: center;
    opacity: 0; transition: opacity var(--t) var(--ease);
  }
  .cx-avatar-wrap:hover .cx-avatar-overlay { opacity: 1; }

  .cx-user-info { flex: 1; min-width: 0; }
  .cx-user-name {
    font-size: 0.875rem; font-weight: 600; color: var(--text-hi);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cx-user-hint { font-size: 0.67rem; color: var(--text-lo); margin-top: 2px; }

  .cx-logout {
    width: 100%; min-height: 44px;
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-mid);
    font-size: 0.875rem;
    transition: all var(--t) var(--ease);
    -webkit-tap-highlight-color: transparent;
  }
  .cx-logout:hover {
    background: rgba(220,38,38,0.07);
    color: #dc2626;
    border-color: rgba(220,38,38,0.18);
  }

  /* ── Top header ─────────────────────────────────── */
  .cx-header {
    height: var(--header-h);
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center;
    padding: 0 14px; gap: 12px;
    flex-shrink: 0;
    box-shadow: var(--shadow-sm);
    z-index: 100;
  }

  .cx-hamburger {
    width: 36px; height: 36px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-xs);
    color: var(--text-mid);
    transition: all var(--t) var(--ease);
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  .cx-hamburger:hover { color: var(--text-hi); border-color: var(--border-md); background: var(--bg-hover); }

  .cx-header-title {
    font-size: 0.95rem; font-weight: 600; color: var(--text-hi);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    flex: 1;
  }

  .cx-fda-badge {
    font-family: 'DM Mono', monospace;
    font-size: 0.6rem; letter-spacing: 0.07em;
    color: var(--cyan);
    background: rgba(8,145,178,0.08);
    border: 1px solid rgba(8,145,178,0.2);
    padding: 3px 8px; border-radius: 20px;
    white-space: nowrap; flex-shrink: 0;
  }
  @media (max-width: 360px) { .cx-fda-badge { display: none; } }

  /* ── Main content ───────────────────────────────── */
  .cx-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
  }

  /* ── Scroll region ──────────────────────────────── */
  .cx-body {
    flex: 1;
    height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior-y: contain;
    scroll-behavior: smooth;
    padding: 20px 16px;
    /* bottom padding accounts for bottom nav + home indicator */
    padding-bottom: calc(var(--bottom-nav-h) + var(--safe-bottom) + 16px);
  }
  /* FIX 2: desktop padding-bottom also respects safe-bottom (e.g. Safari toolbar) */
  @media (min-width: 768px) {
    .cx-body {
      padding: 24px;
      padding-bottom: calc(24px + var(--safe-bottom));
    }
  }

  /* ── Bottom nav — mobile only ───────────────────── */
  .cx-bottom-nav {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 150;
    /* height includes safe area for home indicator */
    height: calc(var(--bottom-nav-h) + var(--safe-bottom));
    display: flex; align-items: stretch;
    justify-content: center;
    background: var(--bg-surface);
    border-top: 1px solid var(--border);
    box-shadow: 0 -2px 12px rgba(0,0,0,0.07);
  }
  @media (min-width: 768px) { .cx-bottom-nav { display: none; } }

  .cx-bn-item {
    flex: 0 0 auto;
    width: 80px;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 3px;
    /* pull items up above the safe-bottom area */
    padding-bottom: var(--safe-bottom);
    background: transparent; border: none;
    color: var(--text-lo);
    font-size: 0.6rem; font-weight: 500; letter-spacing: 0.02em;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    transition: color var(--t) var(--ease);
    position: relative;
    min-height: 44px;
  }
  .cx-bn-item.active { color: var(--blue); }

  .cx-bn-item.logout { color: var(--text-lo); }
  .cx-bn-item.logout:hover,
  .cx-bn-item.logout:active { color: #dc2626; }

  .cx-bn-pill {
    width: 40px; height: 26px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 13px;
    transition: background var(--t) var(--ease);
  }
  .cx-bn-item.active .cx-bn-pill { background: var(--blue-light); }
  .cx-bn-item.logout:hover .cx-bn-pill,
  .cx-bn-item.logout:active .cx-bn-pill { background: rgba(220,38,38,0.08); }

  .cx-bn-item.active::before {
    content: '';
    position: absolute; top: 4px;
    width: 4px; height: 4px; border-radius: 50%;
    background: var(--blue);
  }

  /* ── Page animation ─────────────────────────────── */
  @keyframes cx-fadein {
    from { opacity: 0; transform: translateY(5px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .cx-page { animation: cx-fadein 0.2s var(--ease) both; }

  :focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
`;

/* ─── Inject styles once ─────────────────────────────────────────── */
function useInjectStyles() {
  useEffect(() => {
    const id = 'cx-global-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);
}

/* ─── Swipe left on sidebar to close ────────────────────────────── */
function useSwipeClose(ref, isOpen, onClose) {
  const startX = useRef(null);
  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const el = ref.current;
    const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
    const onTouchEnd   = (e) => {
      if (startX.current !== null && startX.current - e.changedTouches[0].clientX > 56)
        onClose();
      startX.current = null;
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, [isOpen, ref, onClose]);
}

/* ─── Sidebar ────────────────────────────────────────────────────── */
export function Sidebar({ isOpen, onClose }) {
  useInjectStyles();
  const navigate         = useNavigate();
  const location         = useLocation();
  const { user, logout } = useAuth();
  const sidebarRef       = useRef(null);
  const fileInputRef     = useRef(null);
  const [avatar, setAvatar] = useState(getStoredAvatar);

  const initials    = user?.username ? user.username.slice(0, 2).toUpperCase() : 'CX';
  const stableClose = useCallback(() => onClose?.(), [onClose]);

  useSwipeClose(sidebarRef, isOpen, stableClose);

  /* cross-tab avatar sync */
  useEffect(() => {
    const onStorage = (e) => { if (e.key === AVATAR_KEY) setAvatar(e.newValue || null); };
    const onCustom  = (e) => setAvatar(e.detail || null);
    window.addEventListener('storage', onStorage);
    window.addEventListener('avatarUpdated', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('avatarUpdated', onCustom);
    };
  }, []);

  /* click-outside closes sidebar */
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target) &&
        !e.target.closest('[data-sidebar-trigger]')
      ) {
        stableClose();
      }
    };
    const t = setTimeout(() => {
      document.addEventListener('click', handle);
    }, 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handle);
    };
  }, [isOpen, stableClose]);

  /* scroll lock on sidebar open */
  useEffect(() => {
    const body = document.querySelector('.cx-body');
    if (!body) return;
    if (isOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }
    return () => { body.style.overflow = ''; };
  }, [isOpen]);

  /* Escape key */
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === 'Escape') stableClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, stableClose]);

  const go = (path) => { navigate(path); stableClose(); };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) return;
    const reader = new FileReader();
    reader.onload = (ev) => { saveAvatar(ev.target.result); setAvatar(ev.target.result); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <>
      <div
        className="cx-backdrop"
        data-open={String(isOpen)}
        onClick={stableClose}
        aria-hidden="true"
      />

      <aside
        ref={sidebarRef}
        className="cx-sidebar"
        data-open={String(isOpen)}
        aria-label="Main navigation"
      >
        <div className="cx-sb-top">
          <img src="/logo.png" alt="Cytometrix" className="cx-logo" />
        </div>

        <nav className="cx-nav">
          <div className="cx-nav-group">
            <span className="cx-section-lbl">Main</span>
            {navItems.map(({ label, icon: Icon, path }) => (
              <button
                key={path}
                className={`cx-nav-btn ${location.pathname === path ? 'active' : ''}`}
                onClick={() => go(path)}
                aria-current={location.pathname === path ? 'page' : undefined}
              >
                <Icon size={16} aria-hidden="true" />
                {label}
                <ChevronRight size={13} className="cx-chevron" aria-hidden="true" />
              </button>
            ))}
          </div>

          <div className="cx-divider" />

          <div className="cx-nav-group">
            <span className="cx-section-lbl">Settings</span>
            {utilItems.map(({ label, icon: Icon, path }) => (
              <button
                key={path}
                className={`cx-nav-btn ${location.pathname === path ? 'active' : ''}`}
                onClick={() => go(path)}
                aria-current={location.pathname === path ? 'page' : undefined}
              >
                <Icon size={16} aria-hidden="true" />
                {label}
                <ChevronRight size={13} className="cx-chevron" aria-hidden="true" />
              </button>
            ))}
          </div>
        </nav>

        <div className="cx-sb-footer">
          <div className="cx-user-card">
            <div
              className="cx-avatar-wrap"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              aria-label="Change profile photo"
            >
              <div className="cx-avatar">
                {avatar ? <img src={avatar} alt="Profile" /> : initials}
              </div>
              <div className="cx-avatar-overlay" aria-hidden="true">
                <Camera size={12} color="#fff" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </div>

            <div className="cx-user-info">
              <div className="cx-user-name">{user?.username || 'Clinician'}</div>
              <div className="cx-user-hint">Tap photo to change</div>
            </div>
          </div>

          <button
            className="cx-logout"
            onClick={() => { logout(); navigate('/login'); stableClose(); }}
          >
            <LogOut size={15} aria-hidden="true" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}

/* ─── Top header ─────────────────────────────────────────────────── */
export function TopHeader({ title, onMenuClick }) {
  return (
    <header className="cx-header">
      <button
        className="cx-hamburger"
        onClick={onMenuClick}
        data-sidebar-trigger="true"
        aria-label="Open navigation menu"
      >
        <Menu size={18} />
      </button>
      <span className="cx-header-title">{title}</span>
      <span className="cx-fda-badge">● FDA-Aligned</span>
    </header>
  );
}

/* ─── Bottom nav (mobile only via CSS) ──────────────────────────── */
function BottomNav() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { logout }  = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <nav className="cx-bottom-nav" aria-label="Mobile navigation">
      {navItems.map(({ label, icon: Icon, path }) => {
        const active = location.pathname === path;
        return (
          <button
            key={path}
            className={`cx-bn-item ${active ? 'active' : ''}`}
            onClick={() => navigate(path)}
            aria-current={active ? 'page' : undefined}
            aria-label={label}
          >
            <div className="cx-bn-pill">
              <Icon size={18} aria-hidden="true" />
            </div>
            {label === 'New Analysis' ? 'Analysis' : label}
          </button>
        );
      })}

      <button
        className="cx-bn-item logout"
        onClick={handleLogout}
        aria-label="Sign out"
      >
        <div className="cx-bn-pill">
          <LogOut size={18} aria-hidden="true" />
        </div>
        Sign Out
      </button>
    </nav>
  );
}

/* ─── App shell ──────────────────────────────────────────────────── */
export function AppShell({ title, children }) {
  useInjectStyles();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* FIX 1: Lock body scroll so iOS doesn't rubber-band behind the shell */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width    = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width    = '';
    };
  }, []);

  /* Close sidebar on desktop resize */
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setSidebarOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openSidebar  = useCallback(() => setSidebarOpen(true),  []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  return (
    <div className="cx-shell">
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      <div className="cx-main">
        <TopHeader title={title} onMenuClick={openSidebar} />
        <main className="cx-body">
          <div className="cx-page">{children}</div>
        </main>
      </div>

      <BottomNav />
    </div>
  );
}
